import { useCallback, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LUXURY_TIER_LABELS,
  SALE_CATEGORIES,
  type LuxuryTier,
  type Sale,
  type SaleProductExample,
  type SaleStatus,
} from '../../shared/saleFinder';
import { getBrands, getSales, scanBrand, type SaleFilterInput } from '../lib/saleFinder';
import { Icon } from './Icon';

const STATUS_LABELS: Record<SaleStatus, string> = {
  LIVE: 'Live',
  NO_ACTIVE_SALE: 'No sale',
  OUT_OF_STOCK: 'Sold out',
  EXPIRED: 'Expired',
  UNVERIFIED: 'Unverified',
  SCAN_FAILED: 'Scan failed',
};

function statusClass(status: SaleStatus): string {
  if (status === 'LIVE') return 'sale-pill sale-pill--live';
  if (status === 'NO_ACTIVE_SALE' || status === 'OUT_OF_STOCK' || status === 'EXPIRED') {
    return 'sale-pill sale-pill--muted';
  }
  return 'sale-pill sale-pill--grey';
}

function currencyDigits(currency: string): number {
  try {
    return (
      new Intl.NumberFormat('en', { style: 'currency', currency: currency || 'INR' }).resolvedOptions()
        .maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

function formatPrice(minor: number | null, currency: string | null): string {
  if (minor === null) return '';
  const code = currency || 'INR';
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(
      minor / 10 ** currencyDigits(code),
    );
  } catch {
    return String(Math.round(minor / 100));
  }
}

function formatVerified(epoch: number | null): string {
  if (!epoch) return 'Not verified yet';
  const diffMs = Date.now() - epoch * 1000;
  const hours = Math.round(diffMs / 3_600_000);
  if (hours < 1) return 'Verified just now';
  if (hours < 24) return `Verified ${hours}h ago`;
  return `Verified ${Math.round(hours / 24)}d ago`;
}

function topDiscount(sale: Sale): string | null {
  if (sale.discountMax !== null) return `up to ${sale.discountMax}%`;
  const example = sale.products.find((product) => product.discountPercent !== null);
  return example?.discountPercent != null ? `up to ${example.discountPercent}%` : null;
}

function ExamplePrice({ product }: { product: SaleProductExample }) {
  const original = formatPrice(product.originalPriceMinor, product.currency);
  const sale = formatPrice(product.salePriceMinor, product.currency);
  if (!original && !sale) return null;
  return (
    <p className="sale-card__price">
      {original && <span className="sale-card__price-was">{original}</span>}
      {sale && <span className="sale-card__price-now">{sale}</span>}
    </p>
  );
}

function SaleCard({ sale }: { sale: Sale }) {
  const discount = topDiscount(sale);
  const example = sale.products[0];
  const isLive = sale.status === 'LIVE';
  return (
    <article className={`sale-card${isLive ? '' : ' sale-card--dim'}`}>
      <header className="sale-card__head">
        <strong>{sale.brandName}</strong>
        <span className={statusClass(sale.status)}>{STATUS_LABELS[sale.status]}</span>
      </header>
      <p className="sale-card__meta">
        {sale.category ?? 'Store'} · {LUXURY_TIER_LABELS[sale.luxuryTier]}
      </p>
      {isLive && discount ? (
        <>
          <p className="sale-card__discount">{discount}</p>
          {example && <ExamplePrice product={example} />}
        </>
      ) : (
        <p className="sale-card__note">
          {sale.status === 'NO_ACTIVE_SALE' && 'No active discount on the official store right now.'}
          {sale.status === 'OUT_OF_STOCK' && 'Discounted items are currently sold out.'}
          {sale.status === 'EXPIRED' && 'The promotion is no longer active.'}
          {sale.status === 'UNVERIFIED' && 'Could not confirm live status (site may block automated reading).'}
          {sale.status === 'SCAN_FAILED' && 'The last scan could not read this page.'}
        </p>
      )}
      <footer className="sale-card__foot">
        <span className="sale-card__time">
          <Icon name="calendar" size={13} /> {formatVerified(sale.lastVerifiedAt)}
        </span>
        {isLive ? (
          <a href={sale.saleUrl} target="_blank" rel="noreferrer noopener" className="sale-card__link">
            Open sale <Icon name="arrow" size={13} />
          </a>
        ) : (
          <span className="sale-card__time">Watching</span>
        )}
      </footer>
    </article>
  );
}

export function SalesPage() {
  const [sales, setSales] = useState<Sale[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; label: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('LIVE');
  const [category, setCategory] = useState<string>('');
  const [tier, setTier] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters: SaleFilterInput = {};
      if (status) filters.status = status;
      if (category) filters.category = category;
      if (tier) filters.tier = tier;
      const result = await getSales(filters);
      setSales(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sales could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [status, category, tier]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleScan = async () => {
    setScanning(true);
    setMessage(null);
    setError(null);
    try {
      // One request per brand keeps each Worker invocation under Cloudflare's
      // subrequest cap.
      const brands = (await getBrands()).filter((b) => b.enabled);
      if (brands.length === 0) {
        setMessage('No enabled brands to scan.');
        return;
      }
      setProgress({ done: 0, total: brands.length, label: 'Starting…' });
      let live = 0;
      let failed = 0;
      for (let i = 0; i < brands.length; i += 1) {
        const brand = brands[i];
        if (!brand) continue;
        setProgress({ done: i, total: brands.length, label: brand.name });
        try {
          const result = await scanBrand(brand.id);
          live += result.liveFound;
          if (result.liveFound > 0) await load();
        } catch {
          failed += 1;
        }
        setProgress({ done: i + 1, total: brands.length, label: brand.name });
      }
      await load();
      setMessage(
        `Scanned ${brands.length} brand${brands.length === 1 ? '' : 's'} · ${live} live sale${live === 1 ? '' : 's'} found${failed ? ` · ${failed} failed` : ''}.`,
      );
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The scan could not run.');
    } finally {
      setScanning(false);
      setProgress(null);
    }
  };

  return (
    <section className="sales-page">
      <header className="sales-page__head">
        <div>
          <h1>Live sales · India</h1>
          <p>Verified on official brand stores</p>
        </div>
        <div className="sales-page__actions">
          <NavLink className="sales-page__brands-link" to="/sales/brands">
            Manage brands
          </NavLink>
          <button className="primary-button" type="button" onClick={() => void handleScan()} disabled={scanning}>
            <Icon name="search" size={17} />
            {scanning ? 'Scanning…' : 'Scan now'}
          </button>
        </div>
      </header>

      <div className="sales-filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="LIVE">Status: Live</option>
          <option value="">All statuses</option>
          <option value="NO_ACTIVE_SALE">No sale</option>
          <option value="UNVERIFIED">Unverified</option>
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Category">
          <option value="">All categories</option>
          {SALE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c.charAt(0).toUpperCase() + c.slice(1)}
            </option>
          ))}
        </select>
        <select value={tier} onChange={(e) => setTier(e.target.value)} aria-label="Luxury tier">
          <option value="">All tiers</option>
          {(Object.keys(LUXURY_TIER_LABELS) as LuxuryTier[]).map((t) => (
            <option key={t} value={t}>
              {LUXURY_TIER_LABELS[t]}
            </option>
          ))}
        </select>
      </div>

      {progress && (
        <div className="scan-progress" role="status" aria-live="polite">
          <div className="scan-progress__head">
            <span className="scan-progress__label">
              <span className="spinner" aria-hidden="true" /> Scanning {progress.label}…
            </span>
            <span className="scan-progress__pct">
              {Math.round((progress.done / progress.total) * 100)}% ({progress.done}/{progress.total})
            </span>
          </div>
          <div className="scan-progress__track">
            <div
              className="scan-progress__fill"
              style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
            />
          </div>
        </div>
      )}
      {message && !progress && <p className="sales-page__message">{message}</p>}
      {error && <p className="sales-page__error">{error}</p>}

      {loading ? (
        <p className="sales-page__empty">Loading sales…</p>
      ) : sales.length === 0 ? (
        <p className="sales-page__empty">
          No sales to show yet. Hit <strong>Scan now</strong> to check the seeded brands.
        </p>
      ) : (
        <div className="sales-grid">
          {sales.map((sale) => (
            <SaleCard key={sale.id} sale={sale} />
          ))}
        </div>
      )}
    </section>
  );
}
